import * as React from 'react';
import { useReducer, useEffect, useRef, useCallback } from 'react';
import { useFiltersContext } from '../contexts/filters';
import { useSettingsContext } from '../contexts/settings';
import getPosts from '../externals/getPosts';

const actions = {
	SET_POSTS: 'SET_POSTS',
	ADD_POSTS: 'ADD_POSTS',
	SET_PAGE: 'SET_PAGE',
	SET_LOADING: 'SET_LOADING',
	SET_LOADING_HEIGHT: 'SET_LOADING_HEIGHT',
};

const reducer = (state, action) => {
	switch (action.type) {
		case actions.SET_POSTS: {
			return {
				...state,
				posts: action.payload.posts,
				page: action.payload.page,
				maxNumPages: action.payload.max_num_pages,
				foundPosts: action.payload.found_posts,
				loading: false,
			};
		}

		case actions.ADD_POSTS: {
			const posts = action.payload.replace
				? action.payload.posts
				: [...state.posts, ...action.payload.posts];

			return {
				...state,
				posts,
				loading: false,
			};
		}

		case actions.SET_PAGE: {
			if (history.pushState) {
				let newUrl = window.location.href;
				if (action.payload.page > 1) {
					if (window.location.pathname.includes('/page/')) {
						newUrl = window.location.href.replace(
							/\page\/([0-9]+)[\/]?/i,
							`page/${action.payload.page}/`
						);
					} else {
						newUrl =
							window.location.href.replace(/\/$/, '') +
							`/page/${action.payload.page}/`;
					}
				} else {
					newUrl = window.location.href.replace(/page\/([0-9]+)\//i, '');
				}

				if (newUrl !== window.location.href) {
					window.history.pushState({ path: newUrl }, '', newUrl);
				}
			}

			return {
				...state,
				page: action.payload.page,
			};
		}

		case actions.SET_FIRST_RUN: {
			return {
				...state,
				firstRun: action.payload.firstRun,
			};
		}

		case actions.SET_LOADING: {
			return {
				...state,
				loading: action.payload.isLoading,
			};
		}

		case actions.SET_LOADING_HEIGHT: {
			return {
				...state,
				loadingHeight: action.payload.loadingHeight,
			};
		}

		default: {
			return state;
		}
	}
};

const PostsContext = React.createContext();

const PostsProvider = ({ children }) => {
	const settings = useSettingsContext();

	const initialState = {
		posts: [],
		page: settings.initPage,
		maxNumPages: 1,
		foundPosts: 1,
		loadingHeight: null,
		firstRun: true,
	};

	const [state, dispatch] = useReducer(reducer, initialState);
	const { selectedTax, searchQuery } = useFiltersContext();

	useEffect(() => {
		dispatch({ type: actions.SET_LOADING, payload: { isLoading: true } });

		const page = state.firstRun ? settings.initPage : 1;

		const fetchData = async () => {
			const response = await getPosts(
				settings,
				{
					selectedTax,
					searchQuery,
				},
				page
			);

			response.page = page;

			dispatch({ type: actions.SET_POSTS, payload: response });
		};

		// eslint-disable-next-line no-console
		fetchData().catch(console.error);
	}, [selectedTax, searchQuery, state.firstRun, settings]);

	// On page change, add posts. Prevent adding on remounts by storing a ref of prev page.
	const prevPage = useRef([state.page, selectedTax]);
	useEffect(() => {
		const replacePosts = true;

		if (replacePosts && !state.firstRun) {
			const archiveEl = document.querySelector('.post-filters-archive');
			const offsetTop =
				archiveEl.getBoundingClientRect().top +
				(window.scrollY || document.documentElement.scrollTop) -
				150;

			window.scroll({ top: offsetTop, left: 0, behavior: 'smooth' });

			setTimeout(() => {
				dispatch({ type: actions.SET_LOADING, payload: { isLoading: true } });
			}, 10);
		} else {
			dispatch({ type: actions.SET_FIRST_RUN, payload: { firstRun: false } });
		}

		const fetchData = async () => {
			const response = await getPosts(
				settings,
				{
					selectedTax,
					searchQuery,
				},
				state.page
			);

			response.replace = replacePosts;

			prevPage.current = [state.page, selectedTax];
			dispatch({ type: actions.ADD_POSTS, payload: response });
		};

		// eslint-disable-next-line no-console
		fetchData().catch(console.error);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state.page]);

	React.useEffect(() => {
		const onPopState = () => {
			setTimeout(() => {
				let page = 1;
				const pageMatches = location.href.match(/page\/([0-9]+)/i);
				if (null !== pageMatches) {
					page = +pageMatches[1];
				}

				dispatch({ type: actions.SET_PAGE, payload: { page } });
			}, 0);
		};

		window.addEventListener('popstate', onPopState);
		return () => window.removeEventListener('popstate', onPopState);
	}, []);

	const value = {
		posts: state.posts,
		page: state.page,
		maxNumPages: state.maxNumPages,
		loading: state.loading,
		loadingHeight: state.loadingHeight,
		foundPosts: state.foundPosts,
		setPage: (page) => {
			dispatch({ type: actions.SET_PAGE, payload: { page } });
		},
		setLoading: (isLoading) => {
			dispatch({ type: actions.SET_LOADING, payload: { isLoading } });
		},
		setLoadingHeight: useCallback((loadingHeight) => {
			dispatch({ type: actions.SET_LOADING_HEIGHT, payload: { loadingHeight } });
		}, []),
	};

	return <PostsContext.Provider value={value}>{children}</PostsContext.Provider>;
};

const usePostsContext = () => React.useContext(PostsContext);

export { PostsProvider, usePostsContext };
